"use client";

import { createContext, ReactNode, useMemo, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { Spinner } from "@/components/ui/spinner";
import {
  TranslationContextType,
  TranslationData,
} from "@/interface/translation.types";
import { fetchTranslations } from "@/services/translation.service";

const TranslationContext = createContext<TranslationContextType | undefined>(
  undefined
);

interface TranslationProviderProps {
  children: ReactNode;
}

// Function to report missing translation to the API
const reportMissingTranslation = async (key: string): Promise<void> => {
  try {
    await fetch("/api/missing-translation", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ key }),
    });
  } catch (error) {
    // Silently fail - we don't want to break the UI if the API call fails
    console.warn("Failed to report missing translation:", error);
  }
};

export const TranslationProvider: React.FC<TranslationProviderProps> = ({
  children,
}) => {
  const {
    data: translations = {} as TranslationData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["translations"],
    queryFn: fetchTranslations,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 0,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const t = (key: string): string => {
    const translation = (translations as TranslationData)[key];

    // Check if translation is missing or empty
    if (translations && (!translation || translation.trim() === "")) {
      // Report missing translation asynchronously
      if (process.env.NEXT_PUBLIC_LOG_MISSING_TRANSLATION === "true") {
        reportMissingTranslation(key);
      }
      return key; // Return the key as fallback
    }

    return translation;
  };

  const value: TranslationContextType = useMemo(
    () => ({
      translations: translations as TranslationData,
      isLoading,
      error: error || null,
      t,
      refetch,
    }),
    [translations, isLoading, error, refetch]
  );

  // Show loading state while translations are being fetched
  if (isLoading) {
    return (
      <TranslationContext.Provider value={value}>
        <div className="flex items-center justify-center min-h-screen bg-white">
          <div className="flex flex-col items-center space-y-4">
            <Spinner className="h-10 w-10 animate-spin" />
          </div>
        </div>
      </TranslationContext.Provider>
    );
  }

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error("useTranslation must be used within a TranslationProvider");
  }
  return context;
};
